import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";

import { RegistrationFormLayout } from "../FormLayout.tsx";
import { RegistrationFormHeader } from "../FormHeader.tsx";
import { StepNavigation } from "../StepNavigation.tsx";

import { useRegistrationStore } from "@/store/registrationStore.ts";
import { Button } from "@/components/Button.tsx";
import { i18n } from "@/localization";
import { useVerifyAndRequestNewCodeMutation, useRequestVerificationCodeMutation } from "@/api/emailVerification.ts";
import FormErrorMessage from "@/components/FormErrorMessage.tsx";
import { findBackendErrorDetail, handleErrorMessage } from "@/api/utils.ts";
import type { Step2FormData } from "@/schemas/registrationSchemas.ts";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 15; // seconds

const formatDuration = (totalSeconds: number): string => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export default function RegistrationStep2() {
	const { form, nextStep, setField } = useRegistrationStore();
	const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
	const { mutateAsync: verifyAndRequestNewCodeAsync } = useVerifyAndRequestNewCodeMutation();
	const {
		mutateAsync: requestCodeAsync,
		isPending: isResending,
		reset: resetResend
	} = useRequestVerificationCodeMutation();

	const [cooldown, setCooldown] = useState(0);
	const cooldownRef = useRef<NodeJS.Timeout | null>(null);
	const [resendMessage, setResendMessage] = useState<string | null>(null);
	const [lockoutRemaining, setLockoutRemaining] = useState(() =>
		form.lockoutUntilEpochMs ? Math.max(0, Math.ceil((form.lockoutUntilEpochMs - Date.now()) / 1000)) : 0
	);
	const isLocked = lockoutRemaining > 0;

	const {
		control,
		handleSubmit,
		formState: { errors },
		setError,
		setValue
	} = useForm<Step2FormData>({
		defaultValues: {
			code: Array(6).fill("")
		}
	});

	const digits = useWatch({ control, name: "code" });

	const clearCodeInputs = useCallback(
		(focusFirst: boolean) => {
			Array(6)
				.fill("")
				.forEach((_, i) => setValue(`code.${i}`, ""));

			if (focusFirst) {
				inputsRef.current[0]?.focus();
			}
		},
		[setValue]
	);

	const startLockout = useCallback(
		(seconds: number) => {
			setField("lockoutUntilEpochMs", Date.now() + seconds * 1000);
			setLockoutRemaining(seconds);
		},
		[setField]
	);

	const onSubmit = useCallback(
		async (data: Step2FormData) => {
			const code = data.code.join("");

			if (code.length !== CODE_LENGTH) {
				setError("code", { message: i18n("validation.tooSmall") });

				return;
			}

			if (!form.challenge) {
				setError("root", { message: i18n("validation.generalError") });

				return;
			}

			try {
				const res = await verifyAndRequestNewCodeAsync({
					email: form.email,
					code,
					challenge: form.challenge
				});

				if (res?.step1CompletedToken) {
					setField("step1CompletedToken", res.step1CompletedToken);
				}

				nextStep();
			} catch (e) {
				const lockoutDetail =
					findBackendErrorDetail(e, "VERIFICATION_CODE_INVALID") ??
					findBackendErrorDetail(e, "CODE_VERIFICATION_LOCKED");
				const lockoutSeconds = lockoutDetail?.params?.lockoutRemainingSeconds;

				if (lockoutSeconds !== undefined) {
					startLockout(Number(lockoutSeconds));
					clearCodeInputs(false);

					return;
				}

				if (findBackendErrorDetail(e, "VERIFICATION_CODE_INVALID")) {
					clearCodeInputs(true);
				}

				setError("root", { message: handleErrorMessage(e) });
			}
		},
		[
			setError,
			verifyAndRequestNewCodeAsync,
			form.email,
			form.challenge,
			nextStep,
			setField,
			startLockout,
			clearCodeInputs
		]
	);

	const onResendCode = async () => {
		if (!form.email) {
			setError("root", { message: i18n("validation.emailRequired") });

			return;
		}

		try {
			const res = await requestCodeAsync({
				email: form.email,
				firstName: form.firstName,
				lastName: form.lastName
			});
			setField("challenge", res.challenge);
			setField("codeExpirySeconds", res.codeExpirySeconds);
			clearCodeInputs(true);
			setResendMessage(i18n("step2.codeResent"));
			setCooldown(RESEND_COOLDOWN);

			if (cooldownRef.current) {
				clearInterval(cooldownRef.current);
			}

			cooldownRef.current = setInterval(() => {
				setCooldown((prev) => {
					if (prev <= 1) {
						clearInterval(cooldownRef.current!);

						return 0;
					}

					return prev - 1;
				});
			}, 1000);
		} catch (e) {
			setError("root", { message: handleErrorMessage(e) });
			setResendMessage(null);
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		if (isLocked) {
			return;
		}

		const pastedData = e.clipboardData.getData("text").replace(/\D/g, ""); // Remove non-digits

		if (pastedData) {
			const newDigits = Array(6).fill("");

			// Fill up to 6 digits from the pasted data
			for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
				newDigits[i] = pastedData[i];
			}

			// Update all form values
			newDigits.forEach((digit, index) => {
				setValue(`code.${index}`, digit);
			});

			// Focus the next empty input or the last input if all are filled
			const nextEmptyIndex = newDigits.findIndex((digit) => digit === "");
			const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
			inputsRef.current[focusIndex]?.focus();
		}
	};

	const handleDigitChange = (
		index: number,
		e: React.ChangeEvent<HTMLInputElement>,
		onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	) => {
		const value = e.currentTarget.value;

		if (!/^\d?$/.test(value)) {
			return;
		}

		onChange(e);

		if (value && index < 5) {
			inputsRef.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			inputsRef.current[index]?.setAttribute("value", "");
			inputsRef.current[index - 1]?.focus();
		}
	};

	useEffect(() => {
		// Auto-submit when all digits are filled
		if (digits.every((d) => d !== "")) {
			handleSubmit(onSubmit)();
		}
	}, [digits, handleSubmit, onSubmit]);

	useEffect(() => {
		setResendMessage(null);
		resetResend();
	}, [form.email, resetResend, digits]);

	useEffect(() => {
		return () => {
			if (cooldownRef.current) {
				clearInterval(cooldownRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isLocked) {
			return;
		}

		const interval = setInterval(() => {
			setLockoutRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
		}, 1000);

		return () => clearInterval(interval);
	}, [isLocked]);

	useEffect(() => {
		if (!isLocked && form.lockoutUntilEpochMs !== undefined) {
			setField("lockoutUntilEpochMs", undefined);
		}
	}, [isLocked, form.lockoutUntilEpochMs, setField]);

	return (
		<RegistrationFormLayout>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col gap-10">
					<RegistrationFormHeader
						title={i18n("step2.title")}
						description={i18n("step2.description", {
							email: form.email,
							minutes: Math.round((form.codeExpirySeconds ?? 0) / 60)
						})}
					/>

					<div className="flex flex-col gap-2">
						<label className="text-brand-dark/70 text-xs font-semibold">{i18n("step2.inputLabel")}</label>

						<div className="flex justify-between gap-2">
							{Array(6)
								.fill(null)
								.map((_, i) => (
									<Controller
										key={i}
										control={control}
										name={`code.${i}`}
										render={({ field: { onChange, value, ...restOfField } }) => (
											<div className="relative min-w-0 flex-1 sm:flex-none">
												<input
													{...restOfField}
													ref={(el) => {
														inputsRef.current[i] = el;
													}}
													type="text"
													inputMode="numeric"
													maxLength={1}
													disabled={isLocked}
													className="border-brand-dark/10 bg-brand-light-raised focus:ring-brand-primary h-16 w-full rounded-lg border text-center text-2xl font-medium focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-24 sm:w-20"
													value={value}
													onChange={(e) => {
														handleDigitChange(i, e, onChange);
													}}
													onKeyDown={(e) => handleKeyDown(i, e)}
													onPaste={handlePaste}
												/>
												{!value && (
													<span className="text-brand-dark/30 pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 text-4xl font-normal sm:text-5xl">
														-
													</span>
												)}
											</div>
										)}
									/>
								))}
						</div>
					</div>

					{errors.code && <FormErrorMessage>{errors.code.message}</FormErrorMessage>}
					{isLocked ? (
						<FormErrorMessage>
							{i18n("validation.codeVerificationLocked", { time: formatDuration(lockoutRemaining) })}
						</FormErrorMessage>
					) : (
						errors.root && <FormErrorMessage>{errors.root.message}</FormErrorMessage>
					)}

					<Button
						type="button"
						variant="outline"
						onClick={onResendCode}
						loading={isResending}
						disabled={cooldown > 0 || isLocked}
					>
						{cooldown > 0 ? `${i18n("step2.resend")} (${cooldown}s)` : i18n("step2.resend")}
					</Button>
					{resendMessage && (
						<p className="text-brand-primary-900 mt-2 text-center text-sm">{resendMessage}</p>
					)}

					<StepNavigation />
				</div>
			</form>
		</RegistrationFormLayout>
	);
}
